import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, BatteryWarningIcon, BrainIcon } from 'lucide-react';
export function PainPoints() {
  return <section className="py-20 bg-[#EDF2F7]" data-id="element-64">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-65">
        {/* Initial Hook */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-66">
          <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-67">
            Seamos honestas un segundo.
          </h2>

          <div className="bg-white p-8 rounded-xl shadow-sm space-y-6 text-left border border-gray-100" data-id="element-68">
            <p className="text-lg text-gray-700 italic border-l-4 border-gray-300 pl-4" data-id="element-69">
              "Llegás al consultorio. Tenés 4 niños seguidos. El primero tiene
              dislalia, el segundo TEA, el tercero dificultades de lecto y el
              cuarto viene de un colapso en la escuela."
            </p>
            <p className="text-lg text-gray-700 italic border-l-4 border-gray-300 pl-4" data-id="element-70">
              "Y en algún momento entre el primero y el segundo… tu cabeza ya
              está en overdrive."
            </p>
            <p className="text-lg text-gray-700 italic border-l-4 border-gray-300 pl-4" data-id="element-71">
              "¿Qué actividad le pongo hoy? ¿Con esto alcanzo para justificar el
              progreso? ¿Qué le digo a la mamá que me pregunta por qué su hijo
              no mejora? ¿Cómo adapto esto para que funcione con este perfil?"
            </p>
          </div>
        </motion.div>

        {/* The Cycle */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-16" data-id="element-72">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-73">
            Y al final del día llegás a casa con la cabeza partida.
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center" data-id="element-74">
            {['Buscás en Pinterest', 'Abrís tres carpetas de Drive', 'Revisás el cuaderno de planificación que prometiste llevar mejor'].map((item, i) => <div key={i} className="bg-white p-6 rounded-lg shadow-sm border-t-4 border-[#1B3A5C]" data-id="element-75">
                <AlertCircleIcon className="w-8 h-8 text-[#1B3A5C] mx-auto mb-4" data-id="element-76" />
                <p className="font-medium text-gray-800" data-id="element-77">{item}</p>
              </div>)}
          </div>
          <p className="text-center mt-8 text-lg text-gray-700 font-medium" data-id="element-78">
            No porque seas mala profesional. Sino porque nunca tuviste un
            sistema.
          </p>
        </motion.div>

        {/* Burnout Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gray-900 text-white p-8 md:p-12 rounded-2xl shadow-xl relative overflow-hidden" data-id="element-79">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#C9A84C] opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-80" />

          <div className="relative z-10" data-id="element-81">
            <div className="flex items-center gap-4 mb-6" data-id="element-82">
              <BatteryWarningIcon className="w-10 h-10 text-[#C9A84C]" data-id="element-83" />
              <h3 className="text-2xl font-bold" data-id="element-84">
                El problema no son las ganas. Ni la formación. Ni el amor por lo
                que hacés.
              </h3>
            </div>

            <p className="text-xl text-[#C9A84C] mb-8 font-bold" data-id="element-85">
              El problema es que la fonoaudiología infantil te exige tener seis
              especialidades distintas en la cabeza al mismo tiempo
            </p>

            <ul className="space-y-4 mb-8 text-lg" data-id="element-86">
              <li className="flex items-center gap-3" data-id="element-87">
                <span data-id="element-88">🧠</span> Lenguaje y comunicación
              </li>
              <li className="flex items-center gap-3" data-id="element-89">
                <span data-id="element-90">📖</span> Lectoescritura y conciencia fonológica
              </li>
              <li className="flex items-center gap-3" data-id="element-91">
                <span data-id="element-92">⚙️</span> Funciones ejecutivas y cognición
              </li>
              <li className="flex items-center gap-3" data-id="element-93">
                <span data-id="element-94">🌡️</span> Regulación y neurodesarrollo
              </li>
              <li className="flex items-center gap-3" data-id="element-95">
                <span data-id="element-96">👄</span> Sistema estomatognático y habla
              </li>
              <li className="flex items-center gap-3" data-id="element-97">
                <span data-id="element-98">📋</span> Informes, registros y gestión clínica
              </li>
            </ul>

            <p className="text-lg font-medium text-white border-t border-gray-700 pt-6" data-id="element-99">
              Y cada una con tres niveles de edad, 24 sesiones para TEA, 24
              sesiones para TDAH y 24 sesiones para ansiedad, y una familia que
              necesita entender qué pasa.
            </p>
          </div>
        </motion.div>

        {/* The Real Problem */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-16 text-center" data-id="element-100">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6" data-id="element-101">
            <BrainIcon className="w-8 h-8 text-[#1B3A5C]" data-id="element-102" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-103">
            Necesitás tenerlo organizado en un sistema.
          </h3>

          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm max-w-2xl mx-auto" data-id="element-104">
            <p className="font-bold text-gray-800 mb-4" data-id="element-105">
              Las fonoaudiólogas no se cansan de trabajar con niños. Se cansan
              de cargar con todo en la cabeza.
            </p>
            <ul className="text-left space-y-3" data-id="element-106">
              {['Improvisar cada sesión', 'Planificar a medianoche', 'No poder demostrar el progreso', 'Sentir que cada paciente es empezar de cero'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-600" data-id="element-107">
                  <span className="text-[#C9A84C]" data-id="element-108">✖</span>
                  {item}
                </li>)}
            </ul>
          </div>

          <div className="mt-12" data-id="element-109">
            <button onClick={() => document.getElementById('selector-pack')?.scrollIntoView({
            behavior: 'smooth'
          })} className="inline-block bg-[#22C55E] text-white px-8 py-4 rounded-lg font-bold text-lg shadow-lg hover:bg-[#16A34A] transition-all hover:scale-105 cursor-pointer" data-id="element-110">
              Quiero acceder al Mega Pack
            </button>
          </div>
        </motion.div>
      </div>
    </section>;
}